import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, Lock, Mail, User } from "lucide-react";
import { AUTH } from "../lib/testIds";
import { loginWithEmail, signupWithEmail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const formatDetail = (detail) => {
    if (!detail) return null;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : null))
            .filter(Boolean)
            .join(" · ");
    }
    if (typeof detail === "object" && typeof detail.msg === "string") return detail.msg;
    return null;
};

export const AuthForm = ({ mode }) => {
    const isSignup = mode === "signup";
    const { refresh } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const fallback = location.state?.from || "/browse";

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        if (isSignup && !name.trim()) {
            setError("Please enter your name.");
            return;
        }
        if (!email.trim() || !password) {
            setError("Email and password are required.");
            return;
        }
        if (isSignup && password.length < 8) {
            setError("Password must be at least 8 characters.");
            return;
        }
        try {
            setSubmitting(true);
            const user = isSignup
                ? await signupWithEmail({
                      name: name.trim(),
                      email: email.trim(),
                      password,
                  })
                : await loginWithEmail({ email: email.trim(), password });
            await refresh();
            toast.success(isSignup ? "Welcome to Lendly!" : "Welcome back!", {
                description: isSignup
                    ? "Next: finish your profile (zip + address) so you can rent and list items in your neighborhood."
                    : user?.name
                        ? `Signed in as ${user.name}`
                        : undefined,
                duration: 7000,
            });
            navigate(user?.profile_complete ? fallback : "/signup/profile", {
                replace: true,
            });
        } catch (err) {
            const detail =
                formatDetail(err?.response?.data?.detail) ||
                err?.message ||
                "Something went wrong. Please try again.";
            setError(detail);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <form
                onSubmit={handleSubmit}
                data-testid={AUTH.emailForm}
                className="space-y-3"
                noValidate
            >
                {isSignup && (
                    <label className="block">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                            Name
                        </span>
                        <div className="relative mt-1">
                            <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#5a6142]" />
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                data-testid={AUTH.nameInput}
                                placeholder="Jane Park"
                                autoComplete="name"
                                className="w-full rounded-2xl border border-[#2e3220]/15 bg-white/70 py-3 pl-9 pr-3 text-sm text-[#2e3220] placeholder:text-[#5a6142]/60 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                            />
                        </div>
                    </label>
                )}
                <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                        Email
                    </span>
                    <div className="relative mt-1">
                        <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#5a6142]" />
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            data-testid={AUTH.emailInput}
                            placeholder="you@neighbor.com"
                            autoComplete="email"
                            className="w-full rounded-2xl border border-[#2e3220]/15 bg-white/70 py-3 pl-9 pr-3 text-sm text-[#2e3220] placeholder:text-[#5a6142]/60 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                        />
                    </div>
                </label>
                <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                        Password
                    </span>
                    <div className="relative mt-1">
                        <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#5a6142]" />
                        <input
                            type={showPw ? "text" : "password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            data-testid={AUTH.passwordInput}
                            placeholder={isSignup ? "At least 8 characters" : "Your password"}
                            autoComplete={isSignup ? "new-password" : "current-password"}
                            className="w-full rounded-2xl border border-[#2e3220]/15 bg-white/70 py-3 pl-9 pr-10 text-sm text-[#2e3220] placeholder:text-[#5a6142]/60 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPw((v) => !v)}
                            data-testid={AUTH.showPasswordToggle}
                            aria-label={showPw ? "Hide password" : "Show password"}
                            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-[#5a6142] hover:bg-[#2e3220]/5"
                        >
                            {showPw ? (
                                <EyeOff className="h-4 w-4" strokeWidth={2} />
                            ) : (
                                <Eye className="h-4 w-4" strokeWidth={2} />
                            )}
                        </button>
                    </div>
                </label>

                {!isSignup && (
                    <div className="flex justify-end -mt-1">
                        <Link
                            to="/forgot-password"
                            data-testid={AUTH.forgotLink}
                            className="text-xs font-semibold text-[#737a3d] hover:underline"
                        >
                            Forgot password?
                        </Link>
                    </div>
                )}

                {error && (
                    <p
                        data-testid={AUTH.error}
                        role="alert"
                        className="rounded-2xl border border-[#B56C5A]/40 bg-[#B56C5A]/10 px-3 py-2 text-xs font-semibold text-[#7a3a30]"
                    >
                        {error}
                    </p>
                )}

                <button
                    type="submit"
                    disabled={submitting}
                    data-testid={AUTH.submitBtn}
                    className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-60"
                >
                    {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                    {isSignup ? "Create account" : "Sign in"}
                </button>
            </form>

            <p className="mt-5 text-center text-xs text-[#5a6142]">
                {isSignup ? "Already a Lendly Neighbor?" : "New to Lendly?"}{" "}
                <Link
                    to={isSignup ? "/login" : "/signup"}
                    data-testid={AUTH.switchModeLink}
                    className="font-semibold text-[#737a3d] hover:underline"
                >
                    {isSignup ? "Sign in" : "Create an account"}
                </Link>
            </p>
        </>
    );
};

export default AuthForm;
